import React, { useState, useEffect } from 'react';
import { ContactHistoryWithUser, postReleaseNotificationService } from '../../services/postReleaseNotificationService';
import '../../styles/components/ContactHistoryTracker.css';

interface ContactHistoryTrackerProps {
  projectId: string;
  projectTitle: string;
}

interface FilterOptions {
  contactType: 'all' | 'email_template' | 'website_message' | 'discount_offer';
  dateRange: 'all' | 'today' | 'week' | 'month';
  engagementStatus: 'all' | 'engaged' | 'not_engaged';
}

interface EngagementMetrics {
  totalContacts: number;
  emailTemplates: number;
  websiteMessages: number;
  discountOffers: number;
  engagementRate: number;
  clickThroughRate: number;
  conversionRate: number;
}

const ContactHistoryTracker: React.FC<ContactHistoryTrackerProps> = ({
  projectId,
  projectTitle
}) => {
  const [contactHistory, setContactHistory] = useState<ContactHistoryWithUser[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<ContactHistoryWithUser[]>([]);
  const [metrics, setMetrics] = useState<EngagementMetrics>({
    totalContacts: 0,
    emailTemplates: 0,
    websiteMessages: 0,
    discountOffers: 0,
    engagementRate: 0,
    clickThroughRate: 0,
    conversionRate: 0
  });
  const [filters, setFilters] = useState<FilterOptions>({
    contactType: 'all',
    dateRange: 'all',
    engagementStatus: 'all'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContactHistory();
  }, [projectId]);

  useEffect(() => {
    applyFilters();
  }, [contactHistory, filters, searchTerm]);

  const fetchContactHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the service layer instead of direct database queries
      const historyWithUsers = await postReleaseNotificationService.getContactHistory(projectId);

      setContactHistory(historyWithUsers);
      calculateMetrics(historyWithUsers);
    } catch (err: any) {
      console.error('Error fetching contact history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (history: ContactHistoryWithUser[]) => {
    const totalContacts = history.length;
    const emailTemplates = history.filter(h => h.contact_type === 'email_template').length;
    const websiteMessages = history.filter(h => h.contact_type === 'website_message').length;
    const discountOffers = history.filter(h => h.contact_type === 'discount_offer').length;
    
    const engagedContacts = history.filter(h => h.engagement_tracked || h.clicked_through).length;
    const clickedContacts = history.filter(h => h.clicked_through).length;
    const convertedContacts = history.filter(h => h.converted_to_purchase).length;

    setMetrics({
      totalContacts,
      emailTemplates,
      websiteMessages,
      discountOffers,
      engagementRate: totalContacts > 0 ? (engagedContacts / totalContacts) * 100 : 0,
      clickThroughRate: totalContacts > 0 ? (clickedContacts / totalContacts) * 100 : 0,
      conversionRate: totalContacts > 0 ? (convertedContacts / totalContacts) * 100 : 0
    });
  };

  const applyFilters = () => {
    let filtered = [...contactHistory];

    // Apply contact type filter
    if (filters.contactType !== 'all') {
      filtered = filtered.filter(record => record.contact_type === filters.contactType);
    }

    // Apply date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
      }
      
      filtered = filtered.filter(record => 
        new Date(record.contact_date) >= filterDate
      );
    }

    // Apply engagement status filter
    if (filters.engagementStatus !== 'all') {
      if (filters.engagementStatus === 'engaged') {
        filtered = filtered.filter(record => 
          record.engagement_tracked || record.clicked_through || record.converted_to_purchase
        );
      } else {
        filtered = filtered.filter(record => 
          !record.engagement_tracked && !record.clicked_through && !record.converted_to_purchase
        );
      }
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(record =>
        record.user?.username.toLowerCase().includes(searchLower) ||
        record.user?.email.toLowerCase().includes(searchLower) ||
        record.message_content?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredHistory(filtered);
  };

  const handleFilterChange = (filterType: keyof FilterOptions, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const exportContactData = () => {
    const csvHeaders = [
      'Date',
      'Username',
      'Email',
      'Contact Type',
      'Message Content',
      'Engagement Tracked',
      'Clicked Through',
      'Converted to Purchase'
    ];

    const csvData = filteredHistory.map(record => [
      new Date(record.contact_date).toLocaleDateString(),
      record.user?.username || 'N/A',
      record.user?.email || 'N/A',
      record.contact_type,
      record.message_content?.substring(0, 100) || 'N/A',
      record.engagement_tracked ? 'Yes' : 'No',
      record.clicked_through ? 'Yes' : 'No',
      record.converted_to_purchase ? 'Yes' : 'No'
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contact-history-${projectTitle}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const formatContactType = (type: string) => {
    switch (type) {
      case 'email_template':
        return 'Email Template';
      case 'website_message':
        return 'Website Message';
      case 'discount_offer':
        return 'Discount Offer';
      default:
        return type;
    }
  };

  const getEngagementIcon = (record: ContactHistoryWithUser) => {
    if (record.converted_to_purchase) return '💰';
    if (record.clicked_through) return '👆';
    if (record.engagement_tracked) return '👁️';
    return '📧';
  };

  if (loading) {
    return (
      <div className="contact-history-tracker">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading contact history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-history-tracker">
        <div className="error-state">
          <h3>Error Loading Contact History</h3>
          <p>{error}</p>
          <button onClick={fetchContactHistory} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-history-tracker">
      <div className="tracker-header">
        <h2 id="tracker-title">Contact History - {projectTitle}</h2>
        <button 
          onClick={exportContactData} 
          className="export-button"
          aria-label={`Export contact data for ${projectTitle}`}
        >
          📊 Export Data
        </button>
      </div>

      {/* Engagement Metrics */}
      <div className="metrics-grid" role="region" aria-labelledby="metrics-heading">
        <h3 id="metrics-heading" className="sr-only">Engagement Metrics</h3>
        <div className="metric-card" role="img" aria-label={`${metrics.totalContacts} total contacts`}>
          <div className="metric-value">{metrics.totalContacts}</div>
          <div className="metric-label">Total Contacts</div>
        </div>
        <div className="metric-card" role="img" aria-label={`${metrics.engagementRate.toFixed(1)} percent engagement rate`}>
          <div className="metric-value">{metrics.engagementRate.toFixed(1)}%</div>
          <div className="metric-label">Engagement Rate</div>
        </div>
        <div className="metric-card" role="img" aria-label={`${metrics.clickThroughRate.toFixed(1)} percent click-through rate`}>
          <div className="metric-value">{metrics.clickThroughRate.toFixed(1)}%</div>
          <div className="metric-label">Click-Through Rate</div>
        </div>
        <div className="metric-card" role="img" aria-label={`${metrics.conversionRate.toFixed(1)} percent conversion rate`}>
          <div className="metric-value">{metrics.conversionRate.toFixed(1)}%</div>
          <div className="metric-label">Conversion Rate</div>
        </div>
      </div>

      {/* Contact Type Breakdown */}
      <div className="contact-breakdown" role="region" aria-labelledby="breakdown-heading">
        <h3 id="breakdown-heading" className="sr-only">Contact Type Breakdown</h3>
        <div className="breakdown-item">
          <span className="breakdown-label">Email Templates:</span>
          <span className="breakdown-value" aria-label={`${metrics.emailTemplates} email templates sent`}>{metrics.emailTemplates}</span>
        </div>
        <div className="breakdown-item">
          <span className="breakdown-label">Website Messages:</span>
          <span className="breakdown-value" aria-label={`${metrics.websiteMessages} website messages sent`}>{metrics.websiteMessages}</span>
        </div>
        <div className="breakdown-item">
          <span className="breakdown-label">Discount Offers:</span>
          <span className="breakdown-value" aria-label={`${metrics.discountOffers} discount offers sent`}>{metrics.discountOffers}</span>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section" role="region" aria-labelledby="filters-heading">
        <h3 id="filters-heading" className="sr-only">Filter and Search Options</h3>
        <div className="search-box">
          <label htmlFor="contact-search" className="sr-only">Search contacts</label>
          <input
            id="contact-search"
            type="text"
            placeholder="Search by username, email, or message content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            aria-describedby="search-help"
          />
          <div id="search-help" className="sr-only">
            Search through contact history by username, email address, or message content
          </div>
        </div>

        <div className="filters-row">
          <label htmlFor="contact-type-filter" className="sr-only">Filter by contact type</label>
          <select
            id="contact-type-filter"
            value={filters.contactType}
            onChange={(e) => handleFilterChange('contactType', e.target.value)}
            className="filter-select"
            aria-label="Filter by contact type"
          >
            <option value="all">All Contact Types</option>
            <option value="email_template">Email Templates</option>
            <option value="website_message">Website Messages</option>
            <option value="discount_offer">Discount Offers</option>
          </select>

          <label htmlFor="date-range-filter" className="sr-only">Filter by date range</label>
          <select
            id="date-range-filter"
            value={filters.dateRange}
            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            className="filter-select"
            aria-label="Filter by date range"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>

          <label htmlFor="engagement-filter" className="sr-only">Filter by engagement status</label>
          <select
            id="engagement-filter"
            value={filters.engagementStatus}
            onChange={(e) => handleFilterChange('engagementStatus', e.target.value)}
            className="filter-select"
            aria-label="Filter by engagement status"
          >
            <option value="all">All Users</option>
            <option value="engaged">Engaged Users</option>
            <option value="not_engaged">Not Engaged</option>
          </select>
        </div>
      </div>

      {/* Contact History List */}
      <div className="contact-history-list" role="region" aria-labelledby="history-heading">
        <h3 id="history-heading" className="sr-only">Contact History Records</h3>
        {filteredHistory.length === 0 ? (
          <div className="empty-state" role="status">
            <p>No contact records found matching your filters.</p>
            <p>Try adjusting your search criteria or date range.</p>
            {contactHistory.length === 0 && (
              <p>No communications have been sent for this project yet.</p>
            )}
          </div>
        ) : (
          <div className="history-table">
            <div className="table-header">
              <div className="header-cell">User</div>
              <div className="header-cell">Contact Type</div>
              <div className="header-cell">Date</div>
              <div className="header-cell">Engagement</div>
              <div className="header-cell">Message</div>
            </div>
            
            {filteredHistory.map((record) => (
              <div key={record.id} className="table-row">
                <div className="table-cell user-cell">
                  <div className="user-info">
                    <div className="username">{record.user?.username}</div>
                    <div className="email">{record.user?.email}</div>
                  </div>
                </div>
                
                <div className="table-cell contact-type-cell">
                  <span className={`contact-type-badge ${record.contact_type}`}>
                    {formatContactType(record.contact_type)}
                  </span>
                </div>
                
                <div className="table-cell date-cell">
                  {new Date(record.contact_date).toLocaleDateString()}
                  <div className="time">
                    {new Date(record.contact_date).toLocaleTimeString()}
                  </div>
                </div>
                
                <div className="table-cell engagement-cell">
                  <div className="engagement-status">
                    <span className="engagement-icon">
                      {getEngagementIcon(record)}
                    </span>
                    <div className="engagement-details">
                      {record.converted_to_purchase && (
                        <span className="status-badge converted">Converted</span>
                      )}
                      {record.clicked_through && (
                        <span className="status-badge clicked">Clicked</span>
                      )}
                      {record.engagement_tracked && (
                        <span className="status-badge engaged">Engaged</span>
                      )}
                      {!record.engagement_tracked && !record.clicked_through && !record.converted_to_purchase && (
                        <span className="status-badge no-engagement">No Activity</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="table-cell message-cell">
                  <div className="message-preview">
                    {record.message_content ? (
                      record.message_content.length > 100 
                        ? `${record.message_content.substring(0, 100)}...`
                        : record.message_content
                    ) : (
                      <em>No message content</em>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        Showing {filteredHistory.length} of {contactHistory.length} contact records
      </div>
    </div>
  );
};

export default ContactHistoryTracker;